/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactNode, useEffect, useRef, useState } from 'react';
import {
  Box,
  VStack,
  Text,
  Card,
  CardHeader,
  CardBody,
  HStack,
  Button,
  Switch,
  Checkbox,
  IconButton,
  Portal,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody,
  Divider,
  useToast,
} from '@chakra-ui/react';
import { FiInfo, FiBell, FiSmartphone, FiMail, FiClock, FiAlertTriangle } from 'react-icons/fi';
import { useAgreementInfo, useUpdateAgreementInfoMutation } from '@partiu/api/dashboard/hooks';

const STORAGE_KEY = 'dashboard_send_configuration_channels';

type ChannelId = 'plataforma' | 'push' | 'email';
const AVAILABLE_CHANNELS: ChannelId[] = ['plataforma', 'push', 'email'];

interface ChannelInfoPopoverProps {
  title: string;
  description: ReactNode;
  ariaLabel: string;
  contentWidth?: { base: string; md: string };
}

const ChannelInfoPopover = ({ title, description, ariaLabel, contentWidth = { base: 'xs', md: 'sm' } }: ChannelInfoPopoverProps) => (
  <Popover placement="top" isLazy>
    <PopoverTrigger>
      <IconButton aria-label={ariaLabel} size="sm" icon={<FiInfo />} variant="ghost" color="primaryColor" />
    </PopoverTrigger>
    <Portal>
      <PopoverContent w={contentWidth}>
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverHeader fontWeight="semibold">{title}</PopoverHeader>
        <PopoverBody>{description}</PopoverBody>
      </PopoverContent>
    </Portal>
  </Popover>
);

interface ChannelCardProps {
  title: string;
  description: string;
  icon: ReactNode;
  iconBg: string;
  iconColor: string;
  infoTitle: string;
  infoDescription: ReactNode;
  infoAriaLabel: string;
  action: ReactNode;
  badgeText?: string;
}

const ChannelCard = ({
  title,
  description,
  icon,
  iconBg,
  iconColor,
  infoTitle,
  infoDescription,
  infoAriaLabel,
  action,
  badgeText,
}: ChannelCardProps) => (
  <HStack
    justify="space-between"
    align="center"
    p={4}
    borderRadius="lg"
    bg="gray.50"
    borderWidth="1px"
    borderColor="gray.100"
  >
    <HStack spacing={3}>
      <Box p={2} borderRadius="md" bg={iconBg} color={iconColor}>
        {icon}
      </Box>
      <VStack spacing={0} align="start">
        <HStack>
          <Text fontWeight="semibold">{title}</Text>
          <ChannelInfoPopover title={infoTitle} description={infoDescription} ariaLabel={infoAriaLabel} />
        </HStack>
        <Text fontSize="xs" color="gray.500">
          {description}
        </Text>
      </VStack>
    </HStack>
    <HStack>
      {badgeText ? (
        <Text fontSize="xs" color="gray.500">
          {badgeText}
        </Text>
      ) : null}
      {action}
    </HStack>
  </HStack>
);

export function SendConfigurationTab() {
  const [enabledChannels, setEnabledChannels] = useState<ChannelId[]>(['plataforma', 'push']);
  const [isSaving, setIsSaving] = useState(false);
  const emailDialog = useDisclosure();
  const cancelRef = useRef(null);
  const [pendingEmailEnable, setPendingEmailEnable] = useState(false);
  const [hasAcceptedEmailTerms, setHasAcceptedEmailTerms] = useState(false);
  const toast = useToast();
  const { data: agreementInfo } = useAgreementInfo();
  const updateMutation = useUpdateAgreementInfoMutation({
    onSuccess: () => {
      setIsSaving(false);
      toast({
        title: 'Sucesso!',
        description: 'Preferências salvas com sucesso.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: () => {
      setIsSaving(false);
      toast({
        title: 'Erro ao salvar',
        description: 'Não foi possível salvar as preferências. Tente novamente.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const stored = JSON.parse(raw);
        if (Array.isArray(stored)) {
          const filtered = stored.filter((channel: string): channel is ChannelId =>
            AVAILABLE_CHANNELS.includes(channel as ChannelId) && channel !== 'email'
          );
          if (filtered.length) {
            setEnabledChannels(Array.from(new Set([...filtered, 'plataforma'])) as ChannelId[]);
          }
        }
      }
    } catch {
      // noop
    }
  }, []);

  useEffect(() => {
    // Inicializa o estado do email a partir dos dados da nova API
    if (agreementInfo?.enableSendingBookEmail === undefined) return;
    setEnabledChannels(prev => {
      const next = new Set<ChannelId>(prev);
      if (agreementInfo.enableSendingBookEmail) {
        next.add('email');
      } else {
        next.delete('email');
      }
      next.add('plataforma');
      return Array.from(next) as ChannelId[];
    });
  }, [agreementInfo]);

  useEffect(() => {
    // Garante que a notificação de plataforma esteja sempre habilitada
    setEnabledChannels(prev => (prev.includes('plataforma') ? prev : [...prev, 'plataforma']));
  }, []);

  useEffect(() => {
    try {
      // Não salva o email no storage, apenas push e plataforma
      const channelsToStore = enabledChannels.filter(channel => channel !== 'email');
      localStorage.setItem(STORAGE_KEY, JSON.stringify(channelsToStore));
    } catch {
      // noop
    }
  }, [enabledChannels]);

  const handleSave = () => {
    if (!agreementInfo) {
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar as informações. Tente novamente.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    const emailEnabled = enabledChannels.includes('email');

    updateMutation.mutate({
      enable_sending_book_email: emailEnabled,
    });
  };

  const setChannelEnabled = (channel: ChannelId, enabled: boolean) =>
    setEnabledChannels(prev => {
      const next = new Set<ChannelId>(prev);
      if (enabled) next.add(channel);
      else if (channel !== 'plataforma') next.delete(channel);
      // plataforma nunca é removida
      next.add('plataforma');
      return Array.from(next) as ChannelId[];
    });

  const handleTogglePush = (checked: boolean) => {
    setChannelEnabled('push', checked);
  };

  const handleToggleEmail = (checked: boolean) => {
    if (checked) {
      setPendingEmailEnable(true);
      setHasAcceptedEmailTerms(false);
      emailDialog.onOpen();
      return;
    }
    setChannelEnabled('email', false);
  };

  const confirmEnableEmail = () => {
    if (pendingEmailEnable) setChannelEnabled('email', true);
    setPendingEmailEnable(false);
    setHasAcceptedEmailTerms(false);
    emailDialog.onClose();
  };

  const cancelEnableEmail = () => {
    setPendingEmailEnable(false);
    setHasAcceptedEmailTerms(false);
    emailDialog.onClose();
  };

  // Janela de distribuição fixa: as mídias são entregues até o dia 21.
  const getEmailDeliveryMessage = () => 'Os usuários receberão as mídias até o dia 21.';

  return (
    <Box p={4}>
      <VStack spacing={6} align="stretch">
        <Card
          bg="white"
          border="none"
          shadow="lg"
          borderRadius="xl"
          overflow="hidden"
          position="relative"
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            bgGradient: 'linear(to-r, blue.400, purple.400, pink.400)',
          }}
        >
          <CardHeader pb={2}>
            <VStack align="start" spacing={1}>
              <HStack justify="space-between" w="full">
                <VStack align="start" spacing={0}>
                  <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                    ⚙️ Configuração de Envio
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Defina os canais pelos quais os usuários serão notificados sobre os conteúdos recebidos.
                  </Text>
                </VStack>
                <Popover placement="left-start" isLazy>
                  <PopoverTrigger>
                    <IconButton aria-label="Mais informações" icon={<FiInfo />} variant="ghost" color="primaryColor" />
                  </PopoverTrigger>
                  <Portal>
                    <PopoverContent w={{ base: 'sm', md: 'lg' }}>
                      <PopoverArrow />
                      <PopoverCloseButton />
                      <PopoverHeader fontWeight="semibold">Sobre as configurações de envio</PopoverHeader>
                      <PopoverBody>
                        Defina os canais preferenciais para avisar usuários sobre conteúdos entregues. O canal de
                        e‑mail é utilizado apenas para enviar os livros recebidos por e‑mail aos usuários com e‑mail
                        cadastrado. A notificação na plataforma é sempre exibida dentro do ambiente e não pode ser
                        desativada. O push requer app instalado e permissão ativa.
                      </PopoverBody>
                    </PopoverContent>
                  </Portal>
                </Popover>
              </HStack>
            </VStack>
          </CardHeader>
          <CardBody pt={0}>
            <VStack align="stretch" spacing={5}>
              <VStack align="start" spacing={3}>
                <Text fontSize="sm" color="gray.600" fontWeight="semibold">
                  Canais habilitados
                </Text>

                <VStack spacing={3} align="stretch">
                  <ChannelCard
                    title="Notificação na plataforma"
                    description="Mensagens internas visíveis no ambiente"
                    icon={<FiBell />}
                    iconBg="blue.50"
                    iconColor="blue.500"
                    infoTitle="Plataforma"
                    infoDescription="Canal padrão exibido dentro da plataforma. Sempre disponível e não pode ser desabilitado."
                    infoAriaLabel="Informações sobre a notificação na plataforma"
                    badgeText="Sempre ativo"
                    action={<Switch isChecked isDisabled colorScheme="green" />}
                  />

                  <ChannelCard
                    title="Push"
                    description="Requer app e permissão de push"
                    icon={<FiSmartphone />}
                    iconBg="green.50"
                    iconColor="green.500"
                    infoTitle="Push"
                    infoDescription="Envio de notificações para dispositivos com o app instalado e permissões de push ativas."
                    infoAriaLabel="Informações sobre push"
                    action={
                      <Switch
                        isChecked={enabledChannels.includes('push')}
                        onChange={(e) => handleTogglePush(e.target.checked)}
                        colorScheme="green"
                      />
                    }
                  />

                  <ChannelCard
                    title="Email"
                    description="Envia os livros recebidos por e-mail"
                    icon={<FiMail />}
                    iconBg="purple.50"
                    iconColor="purple.500"
                    infoTitle="Email"
                    infoDescription="Envio dos livros recebidos por e-mail para usuários com e-mail cadastrado."
                    infoAriaLabel="Informações sobre email"
                    action={
                      <Switch
                        isChecked={enabledChannels.includes('email')}
                        onChange={(e) => handleToggleEmail(e.target.checked)}
                        colorScheme="green"
                      />
                    }
                  />
                </VStack>
              </VStack>

              <Divider />

              <VStack align="start" spacing={1}>
                <Text fontSize="sm" color="gray.600">
                  Estas preferências serão utilizadas para orientar os canais de notificação dos conteúdos digitais
                  entregues
                </Text>
              </VStack>

              <HStack justify="flex-end">
                <Button onClick={handleSave} isLoading={isSaving} bg="primaryColor" color="white" _hover={{ bg: 'primaryColor' }}>
                  Salvar preferências
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      </VStack>

      <AlertDialog isOpen={emailDialog.isOpen} leastDestructiveRef={cancelRef} onClose={cancelEnableEmail} isCentered>
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader display="flex" alignItems="center" gap={2}>
            <Box as={FiMail} color="primaryColor" />
            Ativar envio por e‑mail
          </AlertDialogHeader>
          <AlertDialogBody>
            <VStack align="stretch" spacing={3}>
              <Box bg="blue.50" p={3} borderRadius="md" borderLeft="4px solid" borderLeftColor="blue.400">
                <HStack align="start" spacing={2}>
                  <Box as={FiMail} color="blue.500" mt="2px" flexShrink={0} />
                  <Text fontSize="13px" lineHeight="1.5" color="gray.700">
                    Ao ativar o canal <Text as="span" fontWeight="bold" color="blue.600">e‑mail</Text>, os livros
                    recebidos serão enviados automaticamente para todos os usuários com e‑mail cadastrado.
                  </Text>
                </HStack>
              </Box>

              <Box bg="green.50" p={3} borderRadius="md" borderLeft="4px solid" borderLeftColor="green.400">
                <HStack align="start" spacing={2}>
                  <Box as={FiClock} color="green.500" mt="2px" flexShrink={0} />
                  <Text fontSize="13px" lineHeight="1.5" fontWeight="semibold" color="green.700">
                    {getEmailDeliveryMessage()}
                  </Text>
                </HStack>
              </Box>

              <Box bg="orange.50" p={3} borderRadius="md" borderLeft="4px solid" borderLeftColor="orange.400">
                <HStack align="start" spacing={2}>
                  <Box as={FiAlertTriangle} color="orange.500" mt="2px" flexShrink={0} />
                  <Text fontSize="13px" lineHeight="1.5" color="orange.800">
                    A entrega depende exclusivamente dos dados do{' '}
                    <Text as="span" fontWeight="bold">
                      ERP
                    </Text>
                    : só recebem os conteúdos os clientes com e‑mail cadastrado e atualizado. Cadastros ausentes,
                    incorretos ou desatualizados não serão notificados, e esta ação afeta todos os usuários e não pode
                    ser desfeita após o envio.
                  </Text>
                </HStack>
              </Box>

              <Box
                p={4}
                mt={1}
                borderRadius="lg"
                borderWidth="1.5px"
                borderColor={hasAcceptedEmailTerms ? 'purple.400' : 'purple.100'}
                bg={hasAcceptedEmailTerms ? 'purple.50' : 'white'}
                boxShadow={hasAcceptedEmailTerms ? '0 0 0 3px rgba(159, 122, 234, 0.15)' : 'none'}
                transition="all 0.2s ease"
              >
                <Text fontSize="2xs" color="purple.500" fontWeight="bold" textTransform="uppercase" mb={2}>
                  Termo de aceite
                </Text>
                <Checkbox
                  isChecked={hasAcceptedEmailTerms}
                  onChange={(e) => setHasAcceptedEmailTerms(e.target.checked)}
                  colorScheme="purple"
                  alignItems="flex-start"
                  width="100%"
                  sx={{
                    '.chakra-checkbox__control': { marginTop: '2px', borderRadius: '4px', bg: 'white' },
                    '.chakra-checkbox__label': { marginLeft: '10px' },
                  }}
                >
                  <Text fontSize="13px" lineHeight="1.6" color="gray.700">
                    Li e autorizo o envio automático dos conteúdos por e‑mail, ciente de que a entrega será feita
                    somente aos clientes com e‑mail cadastrado e devidamente atualizado no ERP, sendo de
                    responsabilidade da empresa manter esses dados corretos.
                  </Text>
                </Checkbox>
              </Box>
            </VStack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <VStack spacing={2} width="100%">
              <Button
                onClick={confirmEnableEmail}
                isDisabled={!hasAcceptedEmailTerms}
                bg="primaryColor"
                color="white"
                _hover={{ bg: 'primaryColor' }}
                width={{ base: '100%', sm: '260px' }}
              >
                Ativar e‑mail
              </Button>
              <Button ref={cancelRef} variant="link" size="sm" onClick={cancelEnableEmail} color="gray.500">
                Cancelar
              </Button>
            </VStack>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Box>
  );
}


